import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { Application, ApplicationApi, Role, RoleApi, RoleRequest } from '@venecia/apis/identity';
import { InputBlock } from '@venecia/molecules';
import { ConfirmDialogService, ErrorMessageService, MessageService, TraceService } from '@venecia/services';

@Component({
  selector: 'app-role-modal',
  imports: [CommonModule, InputBlock, ReactiveFormsModule, TranslateModule],
  templateUrl: './role-modal.html',
  styleUrl: './role-modal.scss',
})
export class RoleModal implements OnChanges {
  private static readonly ROLE_CODE_MAX_LENGTH = 128;

  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly applicationApi = inject(ApplicationApi);
  private readonly roleApi = inject(RoleApi);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  @Input() open = false;
  @Input() role: Role | null = null;
  @Input() allowedApplicationIds: readonly number[] | null = null;
  @Input() lockedApplicationCode: string | null = null;
  @Input() lockedApplicationId: number | null = null;
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly saved = new EventEmitter<void>();

  readonly deleting = signal(false);
  readonly detailMode = signal<'create' | 'edit'>('create');
  readonly saving = signal(false);
  readonly availableApplications = signal<readonly Application[]>([]);
  readonly selectedApplicationId = signal<number | null>(null);
  readonly submitted = signal(false);

  readonly selectedCreatedAt = signal<string | null>(null);
  readonly selectedId = signal<number | null>(null);
  readonly selectedUpdatedAt = signal<string | null>(null);

  readonly isCreateMode = computed(() => this.detailMode() === 'create');
  readonly isEditMode = computed(() => this.detailMode() === 'edit');
  readonly filteredApplications = computed(() => {
    const allowedApplicationIds = this.allowedApplicationIds ?? [];
    if (allowedApplicationIds.length === 0) {
      return this.availableApplications();
    }

    const allowed = new Set(allowedApplicationIds);
    return this.availableApplications().filter((application) => application.id !== null && allowed.has(application.id));
  });

  readonly form = this.formBuilder.nonNullable.group({
    code: ['', [Validators.required, Validators.maxLength(128)]],
    codeSuffix: [''],
    description: ['', [Validators.required, Validators.maxLength(512)]],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.open || (!changes['role'] && !changes['open'])) {
      return;
    }

    this.configureFromInputs();
  }

  closeDetailModal(force = false): void {
    if (!force && (this.saving() || this.deleting())) {
      return;
    }

    this.closed.emit();
  }

  async requestDelete(): Promise<void> {
    const id = this.selectedId();
    if (id === null) {
      return;
    }

    const keys = [
      'pages.roles.confirm-delete.cancel',
      'pages.roles.confirm-delete.confirm',
      'pages.roles.confirm-delete.message',
      'pages.roles.confirm-delete.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    const confirmed = await this.confirmDialogService.open({
      cancelText: i18n['pages.roles.confirm-delete.cancel'],
      confirmText: i18n['pages.roles.confirm-delete.confirm'],
      message: i18n['pages.roles.confirm-delete.message'],
      title: i18n['pages.roles.confirm-delete.title'],
    });

    if (!confirmed) {
      return;
    }

    this.deleting.set(true);
    try {
      await firstValueFrom(this.roleApi.delete(id));
      this.messageService.success(await this.translate('pages.roles.messages.delete-success'));
      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.roles.delete-error'));
    } finally {
      this.deleting.set(false);
    }
  }

  async save(): Promise<void> {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const confirmed = await this.confirmSave();
    if (!confirmed) {
      return;
    }

    this.saving.set(true);
    try {
      const payload = this.toApiModel();
      if (this.isCreateMode()) {
        await firstValueFrom(this.roleApi.create(payload));
        this.messageService.success(await this.translate('pages.roles.messages.create-success'));
      } else {
        await firstValueFrom(this.roleApi.update(this.selectedIdOrThrow(), payload));
        this.messageService.success(await this.translate('pages.roles.messages.update-success'));
      }

      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.roles.save-error'));
    } finally {
      this.saving.set(false);
    }
  }

  hasError(controlName: keyof RoleFormModel, errorName: string): boolean {
    return this.form.controls[controlName].hasError(errorName);
  }

  isInvalid(controlName: keyof RoleFormModel): boolean {
    const control = this.form.controls[controlName];
    return (control.dirty || control.touched || this.submitted()) && control.invalid;
  }

  private configureFromInputs(): void {
    this.submitted.set(false);
    this.configureCodeValidators();

    const role = this.role;
    if (!role) {
      const allowedApplicationIds = this.allowedApplicationIds ?? [];
      const lockedApplicationId = this.lockedApplicationId;
      this.selectedApplicationId.set(
        lockedApplicationId ?? (allowedApplicationIds.length === 1 ? allowedApplicationIds[0] : null),
      );
      this.detailMode.set('create');
      this.selectedId.set(null);
      this.selectedCreatedAt.set(null);
      this.selectedUpdatedAt.set(null);

      this.form.reset({
        code: '',
        codeSuffix: '',
        description: '',
      });
      if (!this.isApplicationLocked()) {
        void this.loadApplications();
      }
      return;
    }

    this.detailMode.set('edit');
    this.selectedApplicationId.set(this.lockedApplicationId ?? role.application?.id ?? null);
    this.selectedId.set(role.id);
    this.selectedCreatedAt.set(role['created-at'] ?? null);
    this.selectedUpdatedAt.set(role['updated-at'] ?? null);

    this.form.reset({
      code: role.code.toUpperCase(),
      codeSuffix: this.extractCodeSuffix(role.code),
      description: role.description,
    });

    if (!this.isApplicationLocked()) {
      void this.loadApplications();
    }
  }

  private selectedIdOrThrow(): number {
    const id = this.selectedId();
    if (id === null) {
      throw new Error('Role id is required for update');
    }
    return id;
  }

  onApplicationSelectionChange(event: Event): void {
    if (this.isApplicationLocked()) {
      return;
    }

    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    this.selectedApplicationId.set(value ? Number(value) : null);
  }

  onCodeInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const value = (target?.value ?? '').toUpperCase();
    this.form.controls.code.setValue(value, { emitEvent: false });
    if (target) {
      target.value = value;
    }
  }

  onCodeSuffixInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const value = (target?.value ?? '').toUpperCase();
    this.form.controls.codeSuffix.setValue(value, { emitEvent: false });
    if (target) {
      target.value = value;
    }
  }

  private toApiModel(): RoleRequest {
    const formValue = this.form.getRawValue();
    const applicationId = this.selectedApplicationId();
    if (applicationId === null) {
      throw new Error('Application id is required for role create/update');
    }

    const code = this.isApplicationLocked()
      ? `${this.roleCodePrefix()}${formValue.codeSuffix.trim().toUpperCase()}`
      : formValue.code.trim().toUpperCase();

    return {
      id: this.selectedId(),
      application: { id: applicationId },
      code,
      description: formValue.description.trim(),
    };
  }

  isApplicationLocked(): boolean {
    return this.lockedApplicationId !== null && this.roleCodePrefix().length > 0;
  }

  roleCodePrefix(): string {
    return this.normalizeRoleCodePrefix(this.lockedApplicationCode);
  }

  roleCodeSuffixMaxLength(): number {
    return Math.max(1, RoleModal.ROLE_CODE_MAX_LENGTH - this.roleCodePrefix().length);
  }

  private configureCodeValidators(): void {
    if (this.isApplicationLocked()) {
      this.form.controls.code.clearValidators();
      this.form.controls.codeSuffix.setValidators([
        Validators.required,
        Validators.maxLength(this.roleCodeSuffixMaxLength()),
      ]);
    } else {
      this.form.controls.code.setValidators([
        Validators.required,
        Validators.maxLength(RoleModal.ROLE_CODE_MAX_LENGTH),
      ]);
      this.form.controls.codeSuffix.clearValidators();
    }

    this.form.controls.code.updateValueAndValidity({ emitEvent: false });
    this.form.controls.codeSuffix.updateValueAndValidity({ emitEvent: false });
  }

  private extractCodeSuffix(code: string): string {
    const normalizedCode = code.trim().toUpperCase();
    const prefix = this.roleCodePrefix();

    if (!prefix) {
      return normalizedCode;
    }

    return normalizedCode.startsWith(prefix)
      ? normalizedCode.slice(prefix.length)
      : normalizedCode;
  }

  private normalizeRoleCodePrefix(value: string | null): string {
    const code = value?.trim().toUpperCase() ?? '';
    return code ? `${code}_` : '';
  }

  private async loadApplications(): Promise<void> {
    try {
      const response = await firstValueFrom(this.applicationApi.search());
      this.availableApplications.set(response.body.data ?? []);
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.roles.applications-load-error'));
    }
  }

  private async translate(key: string): Promise<string> {
    return firstValueFrom(this.translateService.get(key));
  }

  private async translateMap(keys: readonly string[]): Promise<Record<string, string>> {
    return firstValueFrom(this.translateService.get([...keys]));
  }

  private async confirmSave(): Promise<boolean> {
    const keys = [
      'common.confirm-save.cancel',
      'common.confirm-save.confirm',
      'common.confirm-save.message',
      'common.confirm-save.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    return this.confirmDialogService.open({
      cancelText: i18n['common.confirm-save.cancel'],
      confirmText: i18n['common.confirm-save.confirm'],
      message: i18n['common.confirm-save.message'],
      title: i18n['common.confirm-save.title'],
    });
  }
}

interface RoleFormModel {
  code: string;
  codeSuffix: string;
  description: string;
}
